import * as React from 'react';
import type { Meta, StoryObj } from '@storybook/react';
import { css } from '@emotion/css';
import { colors } from '..';
import { spacing, unstable_ScopedSpacingStyles as ScopedSpacingStyles } from '../spacing';

/**
 * Experimental spacing utilities.
 *
 * This is an unstable component, expect breaking changes or complete removal in minor versions.
 *
 * Available spacing values in pixels: 0, 1, 2, 4, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96.
 *
 * **Usage**
 *
 * Wrap the root of your app with the `ScopedSpacingStyles` component (recommended):
 *
 * ```jsx
 * import { unstable_ScopedSpacingStyles as ScopedSpacingStyles } from '@cimpress/react-components';
 *
 * // ...
 *
 * <ScopedSpacingStyles>
 *  <div className="m-16">...</div>
 * </ScopedSpacingStyles>
 * ```
 *
 * Or inject styles globally if you need to access them outside of the `ScopedSpacingStyles` component:
 *
 * ```jsx
 * import { unstable_injectGlobalSpacingStyles } from '@cimpress/react-components';
 *
 * unstable_injectGlobalSpacingStyles();
 *
 * // ...
 *
 * <div className="m-16">...</div>
 * ```
 */
const meta: Meta = {
  title: 'Unstable/Spacing Utilities',
  decorators: [
    Story => (
      <ScopedSpacingStyles>
        <Story />
      </ScopedSpacingStyles>
    ),
  ],
};

export default meta;
type Story = StoryObj;

const marginBoxWrapperStyles = css`
  display: flow-root;
  background-image: linear-gradient(
    135deg,
    ${colors.ocean.base} 10%,
    #0000 0,
    #0000 50%,
    ${colors.ocean.base} 0,
    ${colors.ocean.base} 60%,
    #0000 0,
    #0000
  );
  background-size: 7.07px 7.07px;
  width: max-content;
  border-radius: 4px;

  > div {
    color: #fff;
    background: ${colors.ocean.base};
    border-radius: 4px;
  }
`;

function MarginBoxWrapper({ marginClassName }: { marginClassName: string }) {
  return (
    <div className={marginBoxWrapperStyles}>
      <div className={`p-8 ${marginClassName}`}>{marginClassName}</div>
    </div>
  );
}

/**
 * The spacing utilities are available as classes that you can apply to any element.
 */
export const Margin: Story = {
  render: () => (
    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
      {spacing.map(size => (
        <MarginBoxWrapper key={size} marginClassName={`m-${size}`} />
      ))}
    </div>
  ),
};

/**
 * You can use the breakpoint prefix to apply margin utilities at specific breakpoints.
 *
 */
export const MarginBreakpoints: Story = {
  render: () => (
    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
      <MarginBoxWrapper marginClassName="sm:mt-8" />
      <MarginBoxWrapper marginClassName="md:mt-8" />
      <MarginBoxWrapper marginClassName="lg:mt-8" />
      <MarginBoxWrapper marginClassName="xl:mt-8" />
    </div>
  ),
};

export const MarginSingleSide: Story = {
  render: () => (
    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
      <MarginBoxWrapper marginClassName="mt-16" />
      <MarginBoxWrapper marginClassName="mb-16" />
      <MarginBoxWrapper marginClassName="mr-16" />
      <MarginBoxWrapper marginClassName="ml-16" />
    </div>
  ),
};

export const MarginHorizontal: Story = {
  render: () => <MarginBoxWrapper marginClassName="mx-16" />,
};

export const MarginVertical: Story = {
  render: () => <MarginBoxWrapper marginClassName="my-16" />,
};

export const Padding: Story = {
  render: () => (
    <div style={{ display: 'flex', gap: 8, flexDirection: 'column', color: '#ffffff' }}>
      {spacing.map(size => (
        <div
          key={size}
          className={`p-${size}`}
          style={{ background: colors.ocean.base, width: 'max-content', borderRadius: 4 }}
        >
          p-{size}
        </div>
      ))}
    </div>
  ),
};
